import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { MatButton } from '@angular/material/button';
import {
  MatCard,
  MatCardContent,
  MatCardHeader,
  MatCardTitle,
} from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BackButtonComponent } from '../../../common/back-button/back-button.component';
import { Employee } from '../../../core/models/employee';
import { EmployeeSettings } from '../../../core/models/employee-settings';
import { EmployeeService } from '../../../core/services/employee.service';
import {
  UpdateEmployeeDialogComponent,
  UpdateEmployeeDialogData,
} from '../../dialogs/update-employee-dialog/update-employee-dialog.component';
import {
  UpdateEmployeeSettingsDialogComponent,
  UpdateEmployeeSettingsDialogData,
} from '../../dialogs/update-employee-settings-dialog/update-employee-settings-dialog.component';

@Component({
  selector: 'app-tenant-employee-detail',
  imports: [
    BackButtonComponent,
    MatButton,
    MatCard,
    MatCardContent,
    MatCardHeader,
    MatCardTitle,
    MatProgressSpinner,
  ],
  templateUrl: './tenant-employee-detail.component.html',
  styleUrl: './tenant-employee-detail.component.scss',
  providers: [EmployeeService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TenantEmployeeDetailComponent {
  protected readonly employee = signal<Employee | null>(null);
  protected readonly settings = signal<EmployeeSettings | null>(null);
  protected readonly isLoading = signal(true);

  private readonly tenantIdentifier =
    this.route.snapshot.paramMap.get('tenantIdentifier') ?? '';
  private readonly employeeId =
    this.route.snapshot.paramMap.get('employeeId') ?? '';
  private externalEmployeeIdentifier = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly employeeService: EmployeeService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
  ) {
    this.reload();
  }

  protected openSettingsDialog(): void {
    const currentSettings = this.settings();
    if (!currentSettings) {
      return;
    }

    this.dialog
      .open(UpdateEmployeeSettingsDialogComponent, {
        data: {
          tenantIdentifier: this.tenantIdentifier,
          externalEmployeeIdentifier: this.externalEmployeeIdentifier,
          settings: currentSettings,
        } as UpdateEmployeeSettingsDialogData,
      })
      .afterClosed()
      .subscribe((updatedSettings: EmployeeSettings | undefined) => {
        if (!updatedSettings) {
          return;
        }

        this.settings.set(updatedSettings);
        this.snackBar.open('Profileinstellungen wurden gespeichert.', 'Ok', {
          duration: 3000,
        });
      });
  }

  protected openGeneralDetailsDialog(): void {
    const currentEmployee = this.employee();
    if (!currentEmployee) {
      return;
    }

    this.dialog
      .open(UpdateEmployeeDialogComponent, {
        data: {
          tenantIdentifier: this.tenantIdentifier,
          externalEmployeeIdentifier: currentEmployee.externalIdentifier,
        } as UpdateEmployeeDialogData,
      })
      .afterClosed()
      .subscribe((updatedEmployee: Employee | undefined) => {
        if (!updatedEmployee) {
          return;
        }

        this.employee.set(updatedEmployee);
        this.snackBar.open('Mitarbeiterdetails wurden gespeichert.', 'Ok', {
          duration: 3000,
        });
      });
  }

  protected asBoolean(value?: boolean | null): string {
    return value ? 'Ja' : 'Nein';
  }

  protected asCountryCodes(value?: string[] | null): string {
    return value && value.length > 0 ? value.join(', ') : 'Keine';
  }

  protected asSeconds(value?: number | null): string {
    return value == null ? '—' : `${value} s`;
  }

  private reload(): void {
    this.isLoading.set(true);
    this.employeeService
      .getEmployees(this.tenantIdentifier)
      .pipe(
        switchMap((employees) => {
          const employee = employees.find(
            (currentEmployee) => currentEmployee.employeeId === this.employeeId,
          );
          const externalEmployeeIdentifier = employee?.externalIdentifier;
          if (!externalEmployeeIdentifier) {
            throw new Error('Employee not found');
          }

          this.externalEmployeeIdentifier = externalEmployeeIdentifier;

          return forkJoin({
            employee: this.employeeService.getEmployeeByExternalIdForTenant(
              this.tenantIdentifier,
              externalEmployeeIdentifier,
            ),
            settings: this.employeeService.getEmployeeSettingsForTenant(
              this.tenantIdentifier,
              externalEmployeeIdentifier,
            ),
          });
        }),
        catchError(() => {
          this.isLoading.set(false);
          this.snackBar.open(
            'Mitarbeiterdetails konnten nicht geladen werden.',
            'Ok',
            { duration: 3000 },
          );
          return of(null);
        }),
      )
      .subscribe({
        next: (result) => {
          if (!result) {
            return;
          }

          const { employee, settings } = result;
          if (!employee || !settings) {
            return;
          }

          this.employee.set(employee);
          this.settings.set(settings);
          this.isLoading.set(false);
        },
      });
  }
}
